import { useState, useEffect, useMemo } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useNavigate, useSearchParams } from "react-router-dom";
import { logout as logoutApi } from "@/lib/api/authService";
import { logout } from "@/features/auth/authSlice";
import { useAppDispatch } from "@/app/hooks";
import { Loader2, Plus } from "lucide-react";
import { InviteeFilters } from "../components/InviteeFilters";
import { InviteeTable } from "../components/InviteeTable";
import { InviteeGrid } from "../components/InviteeGrid";
import { EditInviteeDialog } from "../components/EditInviteeDialog";
import { InviteActionsDialog } from "../components/InviteActionsDialog";
import { useInviteeList } from "../hooks/useInviteeList";
import { PER_PAGE_OPTIONS } from "../utils/inviteeConstants";
import { resendInvitation, getInviteeList } from "../services/inviteeService";
import type { Invitee } from "../types/invitee";
import { PageHeader } from "../components/PageHeader";
import {
  showSuccess,
  showError,
  formatApiError,
} from "@/lib/utils/notifications";
import { useUserRole } from "@/hooks/useUserRole";

export function InviteeList() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const inviteCodeFromUrl = searchParams.get("invite_code") ?? undefined;

  // Role-based access (mirrors Angular reports-invites.component.ts)
  const { hasPolicy, hasWriteAccess, isCustomerService, isLibertyRole, orgId, companyId, propertyId } =
    useUserRole();
  const canSeeAddButton = hasPolicy(["invite_add"]);
  const isAddDisabled = isCustomerService; // Angular: button visible but [disabled] for ROLE_CUSTOMER_SERVICE
  const lockedOrgId = orgId != null ? String(orgId) : undefined;
  const lockedCompanyId = companyId != null ? String(companyId) : undefined;
  const lockedPropertyId = propertyId != null ? String(propertyId) : undefined;
  const canEditInvite = hasPolicy(["invite_edit"]);
  const canExport = isLibertyRole; // Available for export button (Angular: liberty_roleslist.includes)
  void canExport;
  const canApplyNow = hasWriteAccess("invite");

  const {
    invitees,
    loading,
    error,
    pagination,
    filters,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch,
  } = useInviteeList(inviteCodeFromUrl);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editInvite, setEditInvite] = useState<Invitee | null>(null);
  const [editParentInvite, setEditParentInvite] = useState<Invitee | null>(
    null,
  );
  const [editType, setEditType] = useState<"edit" | "add">("edit");
  const isMobile = useIsMobile();
  const [isListView, setIsListView] = useState(true);
  const [actionsDialogOpen, setActionsDialogOpen] = useState(false);

  // Mobile (≤768px) → grid view; Desktop → list view (per APPLICANT_PANEL_RESPONSIVE_README)
  useEffect(() => {
    setIsListView(!isMobile);
  }, [isMobile]);
  const [selectedInviteCode, setSelectedInviteCode] = useState<string | null>(
    null,
  );

  // Local state for search debouncing
  const [searchTerm, setSearchTerm] = useState(filters.q || "");

  // Sync local search term with filters when filters change (e.g. from URL)
  useEffect(() => {
    setSearchTerm(filters.q || "");
  }, [filters.q]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only apply if the search term is different from current filter
      if (searchTerm !== (filters.q || "")) {
        applyFilters({ q: searchTerm });
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm, filters.q, applyFilters]);

  const handleResendClick = (inviteCode: string) => {
    setSelectedInviteCode(inviteCode);
    setActionsDialogOpen(true);
  };

  const handleResend = async () => {
    if (!selectedInviteCode) return;
    try {
      await resendInvitation(selectedInviteCode);
      // Show success notification (like Angular)
      showSuccess("Invitation Send Successfully!");
      setActionsDialogOpen(false);
      setSelectedInviteCode(null);
      // Don't refetch - resending doesn't change the list data
    } catch (err: any) {
      // Show error notification (like Angular)
      showError(formatApiError(err), false);
    }
  };

  const handleApply = () => {
    if (!selectedInviteCode) return;
    window.open(`/apply/${selectedInviteCode}`, "_blank");
    setActionsDialogOpen(false);
    setSelectedInviteCode(null);
  };

  const handleEdit = async (inviteCode: string, parentInviteCode?: string) => {
    try {
      if (parentInviteCode) {
        // Co-applicant edit: fetch parent, then find child by invite_code
        const response = await getInviteeList({
          q: parentInviteCode,
          perPage: 1,
        });
        const parent = response.data.find(
          (inv) => inv.invite_code === parentInviteCode,
        );
        const child = parent?.co_applicant?.find(
          (c: any) => c.invite_code === inviteCode,
        );
        if (parent && child) {
          setEditInvite(child as any);
          setEditParentInvite(parent);
          setEditType("edit");
          setEditDialogOpen(true);
          return;
        }
      }

      // Primary invite edit
      const response = await getInviteeList({ q: inviteCode, perPage: 1 });
      const invite = response.data.find(
        (inv) => inv.invite_code === inviteCode,
      );
      if (invite) {
        setEditInvite(invite);
        setEditParentInvite(null);
        setEditType("edit");
        setEditDialogOpen(true);
      }
    } catch (error: any) {
      showError(formatApiError(error), false);
    }
  };

  const handleAddCoApplicant = (invite: Invitee) => {
    // Use the invite from the list; no API call needed
    setEditInvite(invite);
    setEditParentInvite(null);
    setEditType("add");
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    refetch();
    setEditDialogOpen(false);
    setEditInvite(null);
    setEditParentInvite(null);
  };

  const handleApplyNow = (inviteCode: string) => {
    // Angular applyInvitation: logout current tab, open /apply/{code} in new tab
    window.open(`/apply/${inviteCode}`, "_blank");
    void logoutApi().finally(() => {
      dispatch(logout());
      window.location.href = "/login";
    });
  };

  const totalPages = pagination.last || 1;
  const currentPage = pagination.current || 1;

  const PaginationControls = ({
    className,
    placement,
  }: {
    className?: string;
    placement: "header" | "footer";
  }) => {
    return (
      <div
        data-testid={`invitee-list-pagination-${placement}`}
        className={className}
      >
        <div className="flex items-center gap-1.5">
          <button
            data-testid={`invitee-list-pagination-${placement}-first-button`}
            className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            onClick={() => changePage(1)}
            disabled={currentPage === 1}
            title="First"
            type="button"
          >
            &laquo;
          </button>
          <button
            data-testid={`invitee-list-pagination-${placement}-prev-button`}
            className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage === 1}
            title="Previous"
            type="button"
          >
            &lsaquo;
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            const isActive = currentPage === pageNum;
            return (
              <button
                key={pageNum}
                data-testid={`invitee-list-pagination-${placement}-page-${pageNum}-button`}
                className={`min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${isActive
                  ? "border border-button bg-button text-white shadow-md shadow-slate-300/30"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                  }`}
                onClick={() => changePage(pageNum)}
                type="button"
              >
                {pageNum}
              </button>
            );
          })}

          <button
            data-testid={`invitee-list-pagination-${placement}-next-button`}
            className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage === totalPages}
            title="Next"
            type="button"
          >
            &rsaquo;
          </button>
          <button
            data-testid={`invitee-list-pagination-${placement}-last-button`}
            className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            onClick={() => changePage(totalPages)}
            disabled={currentPage === totalPages}
            title="Last"
            type="button"
          >
            &raquo;
          </button>
        </div>
      </div>
    );
  };

  // ... inside InviteeList component
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Count only user-applied filters (exclude role-locked org/company/property so badge shows only when user has applied filters)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.q && String(filters.q).trim()) count++;
    if (filters.org_id && filters.org_id !== lockedOrgId) count++;
    if (filters.company_id && filters.company_id !== lockedCompanyId) count++;
    if (filters.property_id && filters.property_id !== lockedPropertyId) count++;
    if (filters.inviteStatus && filters.inviteStatus.length > 0) count++;
    if (filters.fromDt || filters.toDt) count++;
    return count;
  }, [filters, lockedOrgId, lockedCompanyId, lockedPropertyId]);

  // -- Layout Render --
  return (
    <div
      data-testid="invitee-list-page"
      className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-12"
    >
      {/* -- Main Content Container -- */}
      {/* -- 1. Header Card (Merged Title & Info) -- */}
      {/* -- 1. Page Header -- */}
      <PageHeader />

      {/* -- Main Content Container -- */}
      <div
        data-testid="invitee-list-main"
        className="mx-auto flex w-full min-w-0 flex-col gap-4 px-3 py-4 sm:gap-6 sm:px-6 sm:py-8"
      >
        {/* -- 3. Search & Actions Row -- */}
        <div
          data-testid="invitee-list-toolbar"
          className="flex w-full min-w-0 flex-col gap-4 md:flex-row md:items-center md:justify-between"
        >
          {/* Large Search Input */}
          <div
            data-testid="invitee-list-search-wrap"
            className="relative w-full min-w-0 max-w-lg md:w-1/2"
          >
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
              {/* Search Icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
            <input
              type="text"
              data-testid="invitee-list-search-input"
              placeholder="Search invites..."
              className="w-full pl-12 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15 focus:border-[rgb(21_151_255)] transition-all placeholder:text-slate-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Action Buttons: Filter & Add */}
          <div
            data-testid="invitee-list-toolbar-actions"
            className="flex w-full items-center gap-2 sm:gap-3 md:w-auto"
          >
            <button
              type="button"
              data-testid="invitee-list-filters-open-button"
              onClick={() => setIsFilterOpen(true)}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 bg-white hover:bg-slate-50 border px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all text-sm whitespace-nowrap relative ${activeFilterCount > 0
                  ? "border-[rgb(21_151_255)] text-[rgb(21_151_255)]"
                  : "border-slate-200 text-slate-700"
                }`}
            >
              {/* Filter Icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span
                  data-testid="invitee-list-filters-active-count-badge"
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[rgb(21_151_255)] text-white text-xs font-bold leading-none"
                >
                  {activeFilterCount}
                </span>
              )}
            </button>

            {canSeeAddButton && (
              <button
                type="button"
                data-testid="invitee-list-add-new-invite-button"
                onClick={() =>
                  !isAddDisabled && navigate("/invite/add")
                }
                disabled={isAddDisabled}
                className="flex-[2] md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition-all text-sm whitespace-nowrap bg-[rgb(21_151_255)] text-white enabled:hover:bg-[#0d7acc] enabled:shadow-lg enabled:shadow-[rgba(21,151,255,0.25)] enabled:hover:shadow-xl enabled:hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-55"
              >
                <Plus className="h-5 w-5" />
                Add New Invite
              </button>
            )}
          </div>
        </div>

        {/* -- 4. Table Card -- */}
        <div
          data-testid="invitee-list-results-card"
          className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
        >
          {/* Loading / Error States */}
          {loading && (
            <div
              data-testid="invitee-list-loading"
              className="flex justify-center items-center py-20"
            >
              <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" />
            </div>
          )}

          {error && !loading && (
            <div
              data-testid="invitee-list-error-message"
              className="p-4 sm:p-8 text-center bg-red-50 text-red-600 m-4 rounded-lg"
            >
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {/* -- Pagination Header -- */}
              <div
                data-testid="invitee-list-pagination-header-bar"
                className="flex flex-col items-stretch justify-between gap-3 border-b border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6 sm:py-4"
              >
                <div
                  data-testid="invitee-list-pagination-header-summary"
                  className="text-xs sm:text-sm text-slate-500 font-medium text-center sm:text-left"
                >
                  Page{" "}
                  <span className="text-slate-900 font-bold">
                    {pagination.current}
                  </span>{" "}
                  of{" "}
                  <span className="text-slate-900 font-bold">
                    {pagination.last}
                  </span>
                  <span className="mx-2 text-slate-300">|</span>
                  Total{" "}
                  <span className="text-slate-900 font-bold">
                    {pagination.total}
                  </span>{" "}
                  Records
                </div>

                <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2">
                  <PaginationControls className="" placement="header" />
                </div>
              </div>

              <div
                data-testid="invitee-list-data-scroll"
                className="min-w-0 overflow-x-hidden"
              >
                <div data-testid="invitee-list-data-container" className="min-w-full">
                  {isListView ? (
                    <InviteeTable
                      invitees={invitees}
                      onSort={sort}
                      sortBy={filters.sortBy}
                      orderBy={filters.orderBy}
                      onResend={canEditInvite ? handleResendClick : undefined}
                      onEdit={canEditInvite ? handleEdit : undefined}
                      onAddCoApplicant={
                        canEditInvite ? handleAddCoApplicant : undefined
                      }
                      onApplyNow={handleApplyNow}
                      showActions={canEditInvite}
                    />
                  ) : (
                    <InviteeGrid
                      invitees={invitees}
                      onResend={canEditInvite ? handleResendClick : undefined}
                      onEdit={canEditInvite ? handleEdit : undefined}
                      onAddCoApplicant={
                        canEditInvite ? handleAddCoApplicant : undefined
                      }
                      showActions={canEditInvite}
                    />
                  )}
                </div>
              </div>

              {/* -- 6. Pagination Footer -- */}
              <div
                data-testid="invitee-list-pagination-footer-bar"
                className="flex flex-col items-stretch justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6 sm:py-4"
              >
                <div
                  data-testid="invitee-list-pagination-footer-summary"
                  className="text-xs sm:text-sm text-slate-500 font-medium text-center sm:text-left"
                >
                  Page{" "}
                  <span className="text-slate-900 font-bold">
                    {pagination.current}
                  </span>{" "}
                  of{" "}
                  <span className="text-slate-900 font-bold">
                    {pagination.last}
                  </span>
                  <span className="mx-2 text-slate-300">|</span>
                  Total{" "}
                  <span className="text-slate-900 font-bold">
                    {pagination.total}
                  </span>{" "}
                  Records
                </div>

                <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2">
                  <PaginationControls className="" placement="footer" />

                  {/* Per Page Select */}
                  <div className="ml-0 flex items-center gap-2 sm:ml-4">
                    <span
                      data-testid="invitee-list-per-page-label-footer"
                      className="text-xs font-semibold uppercase tracking-wide text-slate-400"
                    >
                      Rows
                    </span>
                    <select
                      data-testid="invitee-list-per-page-select-footer"
                      className="block rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-700 focus:border-button focus:outline-none focus:ring-2 focus:ring-button/30"
                      value={filters.perPage || 15}
                      onChange={(e) =>
                        changePerPage(parseInt(e.target.value, 10))
                      }
                    >
                      {PER_PAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Filter Drawer (Portal) */}
      <InviteeFilters
        filters={filters}
        onFiltersChange={(f, meta) => applyFilters(f, meta)}
        onReset={resetFilters}
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      />

      {/* Dialogs */}
      <EditInviteeDialog
        isOpen={editDialogOpen}
        invite={editInvite}
        parentInvite={editParentInvite}
        type={editType}
        onClose={() => {
          setEditDialogOpen(false);
          setEditInvite(null);
          setEditParentInvite(null);
        }}
        onSuccess={handleEditSuccess}
      />

      <InviteActionsDialog
        isOpen={actionsDialogOpen}
        onClose={() => {
          setActionsDialogOpen(false);
          setSelectedInviteCode(null);
        }}
        onResend={handleResend}
        onApply={handleApply}
        showApplyNow={canApplyNow}
      />
    </div>
  );
}
